// Static SEO landing page definitions. Each entry describes one URL slug,
// its language, the FAQ schema (English variants only — Google translates the
// rest), and which live data widget to embed. Registered in App.js and used by
// backend sitemap generator (mirror this list in server.py if edited).

// Slug pattern: 6 topics × 3 langs = 18 pages.
// Only EN pages carry FAQ JSON-LD (per spec).

const SITE = "https://islandsdaily.com";

// ------------ Aruba news feed --------------------------------------
const ARUBA_NEWS_EN = {
  slug: "aruba-news-english",
  lang: "en",
  embedType: "latest-news",
  embedProps: { island: "aruba", limit: 6 },
  title: "Aruba News in English — Daily Summaries from Local Sources | IslandsDaily",
  description:
    "Aruba news translated into English every morning. Politics, tourism, crime, business — aggregated from 24ora, Noticia Cla, AWE24 and Mas Noticia. Free.",
  h1: "Aruba News in English",
  intro:
    "Every morning we translate Aruba's biggest headlines into English so visitors, expats and remote workers can read what the island is talking about — no Papiamento or Dutch needed.",
  sections: [
    {
      h2: "Where the news comes from",
      body: "IslandsDaily pulls fresh headlines directly from Aruba's four leading independent news sites: 24ora, Noticia Cla, AWE24 and Mas Noticia. Each story is scraped within minutes of publication, summarised into three or four sentences by Claude (Anthropic's language model), then rendered in English above a link to the original article. Nothing is rewritten in a way that changes the facts — the summary preserves names, numbers and quotes so you can verify against the source in one click.",
    },
    {
      h2: "Why this exists",
      body: "The Papiamento and Dutch press covers Aruba far better than any English-language outlet, but that reporting is essentially invisible to anyone who doesn't speak the language. Google Translate helps in a pinch, but sixty tabs and awkward machine translations are not how anyone wants to catch up on the news. IslandsDaily is the version we wanted for ourselves: a single English morning briefing that reads like a real newsletter.",
    },
    {
      h2: "What's covered",
      body: "Politics and government, tourism policy, cruise arrivals, small-business news, crime and safety alerts, weather and hurricane advisories, community events, sports and culture. Each article carries the source name, a link out, and a translation-quality note so you always know who reported the story first.",
    },
    {
      h2: "How to get the daily briefing",
      body: "The full Aruba feed lives on this site — refresh any time — and there is a free WhatsApp Daily Digest that arrives at 08:00 AST every morning with the top five stories. No account, no email required, and one tap unsubscribes.",
    },
  ],
  faq: [
    {
      q: "Is Aruba news in English published anywhere else?",
      a: "Aruba Today prints a small English weekly aimed at tourists, but there is no daily English news source that translates the local Papiamento and Dutch press. IslandsDaily fills that gap.",
    },
    {
      q: "How often is the feed updated?",
      a: "The scraper runs every 15 minutes. Most stories reach IslandsDaily within an hour of appearing on 24ora, Noticia Cla, AWE24 or Mas Noticia.",
    },
    {
      q: "Are the summaries accurate?",
      a: "Summaries are generated by Claude and then flagged if the translation confidence is low — those flagged stories are re-run automatically. The source link is always attached so you can verify.",
    },
    {
      q: "Is IslandsDaily free?",
      a: "Yes. The site and the WhatsApp Daily Digest are both free. IslandsDaily is funded by local advertising, not subscriptions.",
    },
  ],
  relatedLinks: [
    { href: "/aruba", label: "All Aruba news →" },
    { href: "/aruba-daily-briefing", label: "Aruba Daily Briefing" },
    { href: "/pharmacy-on-duty-aruba", label: "Pharmacy on Duty — Aruba" },
    { href: "/cruise-ships-today-aruba", label: "Cruise Ships in Aruba Today" },
  ],
};

const ARUBA_NEWS_NL = {
  slug: "nieuws-aruba",
  lang: "nl",
  embedType: "latest-news",
  embedProps: { island: "aruba", limit: 6 },
  title: "Aruba Nieuws in het Nederlands — Dagelijkse Samenvattingen | IslandsDaily",
  description:
    "Het belangrijkste Aruba-nieuws elke ochtend in het Nederlands. Politiek, toerisme, criminaliteit, economie — samengevat uit 24ora, Noticia Cla, AWE24 en Mas Noticia. Gratis.",
  h1: "Aruba nieuws in het Nederlands",
  intro:
    "Elke ochtend vertalen we de belangrijkste Aruba-koppen naar het Nederlands, zodat Nederlanders, ABC-eilanders en Caribisch Nederland kunnen lezen wat er echt speelt op het eiland — zonder Papiaments te hoeven begrijpen.",
  sections: [
    {
      h2: "Waar het nieuws vandaan komt",
      body: "IslandsDaily haalt verse koppen direct van de vier grootste onafhankelijke Aruba-nieuwssites: 24ora, Noticia Cla, AWE24 en Mas Noticia. Elke berichtgeving wordt binnen enkele minuten na publicatie gescand, in drie tot vier zinnen samengevat door Claude (het taalmodel van Anthropic) en vervolgens in het Nederlands weergegeven boven een link naar het originele artikel. Namen, getallen en citaten blijven bewaard — u kunt met één klik verifiëren.",
    },
    {
      h2: "Waarom dit bestaat",
      body: "De Papiamentstalige en Nederlandstalige pers dekt Aruba beter dan welk Engelstalig medium dan ook, maar voor Nederlanders in het thuisland en veel expats is dat aanbod versnipperd. IslandsDaily brengt alles samen in één ochtendoverzicht dat leest als een echte nieuwsbrief.",
    },
    {
      h2: "Wat wordt gedekt",
      body: "Politiek en bestuur, toerismebeleid, cruise-aankomsten, midden- en kleinbedrijf, veiligheid en criminaliteit, weer en orkaanwaarschuwingen, evenementen, sport en cultuur. Elk artikel bevat de bron, een uitgaande link en een vertaalkwaliteitsindicator.",
    },
    {
      h2: "Hoe u de dagelijkse briefing ontvangt",
      body: "De volledige Aruba-feed staat hierboven — ververs op elk moment — en er is een gratis WhatsApp Dagelijkse Briefing die elke ochtend om 08:00 AST aankomt met de top vijf verhalen. Geen account, geen e-mail, één tik om af te melden.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Alle Aruba-nieuws →" },
    { href: "/aruba-dagelijkse-briefing", label: "Aruba Dagelijkse Briefing" },
    { href: "/apotheek-dienst-aruba", label: "Apotheek van Dienst — Aruba" },
    { href: "/cruiseschepen-aruba-vandaag", label: "Cruiseschepen in Aruba vandaag" },
  ],
};

const ARUBA_NEWS_ES = {
  slug: "noticias-aruba",
  lang: "es",
  embedType: "latest-news",
  embedProps: { island: "aruba", limit: 6 },
  title: "Noticias de Aruba en Español — Resúmenes Diarios | IslandsDaily",
  description:
    "Las principales noticias de Aruba cada mañana en español. Política, turismo, seguridad, economía — resumidas desde 24ora, Noticia Cla, AWE24 y Mas Noticia. Gratis.",
  h1: "Noticias de Aruba en español",
  intro:
    "Cada mañana traducimos los principales titulares de Aruba al español para que hispanohablantes en Venezuela, Colombia, República Dominicana y el resto del Caribe puedan leer lo que realmente está pasando en la isla — sin necesidad de entender papiamento u holandés.",
  sections: [
    {
      h2: "De dónde vienen las noticias",
      body: "IslandsDaily obtiene titulares frescos directamente de los cuatro principales sitios de noticias independientes de Aruba: 24ora, Noticia Cla, AWE24 y Mas Noticia. Cada nota se procesa minutos después de su publicación, se resume en tres o cuatro oraciones con Claude (el modelo de lenguaje de Anthropic) y luego se presenta en español sobre un enlace al artículo original. Nombres, cifras y citas se mantienen — puede verificar con un clic.",
    },
    {
      h2: "Por qué existe",
      body: "La prensa en papiamento y holandés cubre Aruba mejor que cualquier medio en español, pero ese trabajo es prácticamente invisible para quien no habla esos idiomas. Google Translate ayuda a medias. IslandsDaily es la versión que queríamos para nosotros: un solo resumen matutino en español que se lee como un boletín real.",
    },
    {
      h2: "Qué cubrimos",
      body: "Política y gobierno, política turística, llegadas de cruceros, PyMEs, seguridad ciudadana, clima y alertas de huracanes, eventos comunitarios, deportes y cultura. Cada artículo lleva el nombre de la fuente, un enlace de salida y una nota de calidad de traducción.",
    },
    {
      h2: "Cómo recibir el resumen diario",
      body: "El feed completo de Aruba vive en esta página — actualícela en cualquier momento — y hay un Resumen Diario por WhatsApp gratuito que llega a las 08:00 AST cada mañana con las cinco historias principales. Sin cuenta, sin correo, un toque para darse de baja.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Todas las noticias de Aruba →" },
    { href: "/resumen-diario-aruba", label: "Resumen Diario de Aruba" },
    { href: "/farmacia-turno-aruba", label: "Farmacia de Turno — Aruba" },
    { href: "/cruceros-aruba-hoy", label: "Cruceros en Aruba hoy" },
  ],
};

// ------------ Curaçao news feed ------------------------------------
const CURACAO_NEWS_EN = {
  slug: "curacao-news-english",
  lang: "en",
  embedType: "latest-news",
  embedProps: { island: "curacao", limit: 6 },
  title: "Curaçao News in English — Daily Summaries from Local Sources | IslandsDaily",
  description:
    "Curaçao news translated into English every morning. Politics, tourism, business, safety — aggregated from Nu.cw, Knipselkrant Curaçao and Curaçao Chronicle. Free.",
  h1: "Curaçao News in English",
  intro:
    "IslandsDaily translates Curaçao's biggest stories into English every morning so visitors, retirees and remote workers on the island can read what Willemstad is actually talking about — without waiting for a foreign wire service to notice.",
  sections: [
    {
      h2: "Where the news comes from",
      body: "We pull directly from the three most active independent Curaçao news outlets: Nu.cw (news portal), Knipselkrant Curaçao (long-form political and business coverage) and Curaçao Chronicle (English-language reporting complemented with our Papiamento coverage). Each story is scraped, summarised by Claude and shown here above a link to the source article.",
    },
    {
      h2: "Why this exists",
      body: "Most English-language coverage of Curaçao is either tourism marketing or one-off cruise-line press releases. Actual reporting — parliamentary debates, refinery decisions, currency policy, ISLA cleanup, immigration — happens almost entirely in Papiamento and Dutch. IslandsDaily reads those sources for you and delivers the substance in English every morning.",
    },
    {
      h2: "What's covered",
      body: "Government and elections, RdK / refinery news, tourism and cruise, economy and the guilder, crime and safety, weather and hurricane season, sports, arts and culture. Every story links back to the original so you can verify.",
    },
    {
      h2: "How to get the daily briefing",
      body: "Bookmark this page for the live English feed or subscribe to the free WhatsApp Daily Digest — five stories, delivered at 08:00 AST every morning, unsubscribe with one tap.",
    },
  ],
  faq: [
    {
      q: "Where can I read Curaçao news in English?",
      a: "IslandsDaily is the only daily source that translates Papiamento- and Dutch-language reporting from Curaçao into English. Curaçao Chronicle publishes some original English stories; we aggregate those alongside our translations.",
    },
    {
      q: "Does IslandsDaily cover refinery and ISLA news?",
      a: "Yes. RdK, refinery-lease negotiations and environmental reporting from the ISLA site are core beats — that's exactly the kind of story English readers usually miss.",
    },
    {
      q: "How current are the stories?",
      a: "The scraper runs every 15 minutes. Most Curaçao stories arrive in English within an hour of publication.",
    },
    {
      q: "Is there a cost?",
      a: "No. The site and the WhatsApp Daily Digest are free.",
    },
  ],
  relatedLinks: [
    { href: "/curacao", label: "All Curaçao news →" },
    { href: "/caribbean-morning-briefing", label: "Caribbean Morning Briefing" },
    { href: "/curacao/cruise-schedule", label: "Curaçao Cruise Schedule" },
  ],
};

const CURACAO_NEWS_NL = {
  slug: "nieuws-curacao",
  lang: "nl",
  embedType: "latest-news",
  embedProps: { island: "curacao", limit: 6 },
  title: "Curaçao Nieuws in het Nederlands — Dagelijkse Samenvattingen | IslandsDaily",
  description:
    "Het belangrijkste Curaçao-nieuws elke ochtend in het Nederlands. Politiek, toerisme, economie — samengevat uit Nu.cw, Knipselkrant Curaçao en Curaçao Chronicle. Gratis.",
  h1: "Curaçao nieuws in het Nederlands",
  intro:
    "IslandsDaily brengt elke ochtend de belangrijkste Curaçao-verhalen in het Nederlands zodat Nederlanders en Nederlandssprekende expats snel kunnen zien wat er in Willemstad speelt.",
  sections: [
    {
      h2: "Waar het nieuws vandaan komt",
      body: "We halen direct van de drie meest actieve onafhankelijke Curaçao-nieuwsbronnen: Nu.cw, Knipselkrant Curaçao en Curaçao Chronicle. Elke berichtgeving wordt gescand, samengevat door Claude en hier getoond boven een link naar het originele artikel.",
    },
    {
      h2: "Waarom dit bestaat",
      body: "Voor veel Nederlanders in Nederland zelf is Curaçao-nieuws lastig te volgen: verspreide bronnen, deels Papiamentstalig, geen centrale plek. IslandsDaily leest die bronnen voor u en levert elke ochtend één overzicht.",
    },
    {
      h2: "Wat wordt gedekt",
      body: "Regering en verkiezingen, RdK / raffinaderij, toerisme en cruise, economie en de gulden, veiligheid, weer en orkaanseizoen, sport en cultuur. Elk verhaal linkt naar de originele bron.",
    },
    {
      h2: "Hoe u de dagelijkse briefing ontvangt",
      body: "Bookmark deze pagina voor de live feed of abonneer u op de gratis WhatsApp Dagelijkse Briefing — vijf verhalen, elke ochtend om 08:00 AST, één tik om af te melden.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/curacao", label: "Alle Curaçao-nieuws →" },
    { href: "/caribisch-ochtend-briefing", label: "Caribische Ochtendbriefing" },
    { href: "/curacao/cruise-schedule", label: "Curaçao cruiseschema" },
  ],
};

const CURACAO_NEWS_ES = {
  slug: "noticias-curazao",
  lang: "es",
  embedType: "latest-news",
  embedProps: { island: "curacao", limit: 6 },
  title: "Noticias de Curazao en Español — Resúmenes Diarios | IslandsDaily",
  description:
    "Las principales noticias de Curazao cada mañana en español. Política, turismo, economía — resumidas desde Nu.cw, Knipselkrant Curaçao y Curaçao Chronicle. Gratis.",
  h1: "Noticias de Curazao en español",
  intro:
    "IslandsDaily traduce cada mañana las principales historias de Curazao al español para que hispanohablantes en Venezuela, Colombia y el resto del Caribe puedan seguir lo que realmente pasa en Willemstad.",
  sections: [
    {
      h2: "De dónde vienen las noticias",
      body: "Extraemos directamente de las tres fuentes independientes más activas de Curazao: Nu.cw, Knipselkrant Curaçao y Curaçao Chronicle. Cada nota se procesa, se resume con Claude y se presenta aquí sobre un enlace al artículo original.",
    },
    {
      h2: "Por qué existe",
      body: "La mayor parte de la información sobre Curazao en español es marketing turístico. La cobertura política, económica y social ocurre casi exclusivamente en papiamento y holandés. IslandsDaily lee esas fuentes por usted y entrega la sustancia en español cada mañana.",
    },
    {
      h2: "Qué cubrimos",
      body: "Gobierno y elecciones, RdK / refinería, turismo y cruceros, economía y el florín, seguridad, clima y temporada de huracanes, deportes y cultura. Cada historia enlaza a la fuente original.",
    },
    {
      h2: "Cómo recibir el resumen diario",
      body: "Marque esta página para el feed en vivo o suscríbase al Resumen Diario por WhatsApp gratuito — cinco historias, cada mañana a las 08:00 AST, un toque para darse de baja.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/curacao", label: "Todas las noticias de Curazao →" },
    { href: "/resumen-matutino-caribe", label: "Resumen Matutino del Caribe" },
    { href: "/curacao/cruise-schedule", label: "Cruceros de Curazao" },
  ],
};

// ------------ Pharmacy on duty Aruba -------------------------------
const PHARMACY_EN = {
  slug: "pharmacy-on-duty-aruba",
  lang: "en",
  embedType: "pharmacy-duty",
  embedProps: { island: "aruba" },
  title: "Pharmacy on Duty in Aruba Today — Live Rotation | IslandsDaily",
  description:
    "Which pharmacy is on duty in Aruba tonight? Updated daily with name, address, phone and opening hours. Direct call and Google Maps links.",
  h1: "Pharmacy on duty in Aruba today",
  intro:
    "Aruba's pharmacies rotate a night and weekend shift so at least one 'botica di turno' is always open. This page shows which pharmacy has the shift right now, with the phone number, address and directions.",
  sections: [
    {
      h2: "How the rotation works",
      body: "Aruba's Association of Pharmacies publishes a weekly rotation. During regular working hours all pharmacies are open; from about 19:00 through the following morning, and all day Sunday, only the pharmacy 'op wacht' (on duty) is available for emergency prescriptions. Some pharmacies are certified 24-hour operations and are staffed continuously — those are labelled 'Open 24 hours' in the card above.",
    },
    {
      h2: "What you can do from this page",
      body: "Tap the phone number to call directly — the pharmacist can confirm your prescription is in stock before you drive across the island. Tap 'Get Directions' to open the address in Google Maps. If your prescription needs a specific brand or dosage that isn't guaranteed on the rotation night, call ahead: pharmacies routinely help each other and can point you to whoever has the medication on hand.",
    },
    {
      h2: "What to bring",
      body: "Bring your original prescription, ID or passport, and — if you have Aruba's public insurance (AZV) — your AZV card. Cash and major cards are accepted. Prices for prescription medication in Aruba are regulated; for most standard prescriptions you will pay a small dispensing fee if you are AZV-covered.",
    },
    {
      h2: "Emergency numbers",
      body: "For a life-threatening emergency call 911. For non-urgent medical advice outside pharmacy hours the Horacio Oduber Hospital emergency room is open 24/7 in Oranjestad. Poison control questions are handled by hospital emergency staff.",
    },
  ],
  faq: [
    {
      q: "Which pharmacy is open tonight in Aruba?",
      a: "The pharmacy currently on duty is shown live in the card above — pulled from the official weekly rotation. Tap the address for directions or the phone number to call.",
    },
    {
      q: "Are Aruba pharmacies open on Sundays?",
      a: "Yes. There is always one 'botica di turno' open on Sundays as part of the rotation. The card above shows Sunday's pharmacy from midnight.",
    },
    {
      q: "Is there a 24-hour pharmacy in Aruba?",
      a: "Selected pharmacies run 24-hour operations. If today's on-duty pharmacy is one of them, the card will show 'Open 24 hours'.",
    },
    {
      q: "Do I need to bring my AZV card?",
      a: "If you have AZV coverage, yes — otherwise you pay the full retail price. A passport or Aruban ID is also usually required for controlled substances.",
    },
  ],
  relatedLinks: [
    { href: "/aruba", label: "All Aruba news →" },
    { href: "/aruba-daily-briefing", label: "Aruba Daily Briefing" },
    { href: "/cruise-ships-today-aruba", label: "Cruise Ships in Aruba Today" },
  ],
};

const PHARMACY_NL = {
  slug: "apotheek-dienst-aruba",
  lang: "nl",
  embedType: "pharmacy-duty",
  embedProps: { island: "aruba" },
  title: "Apotheek van Dienst in Aruba — Live Rotatie | IslandsDaily",
  description:
    "Welke apotheek is vanavond op Aruba open? Elke dag bijgewerkt met naam, adres, telefoon en openingstijden. Directe bel- en routelinks.",
  h1: "Apotheek van dienst in Aruba vandaag",
  intro:
    "De apotheken van Aruba draaien een avond- en weekenddienst zodat er altijd één 'botica di turno' open is. Deze pagina toont welke apotheek nu dienst heeft, met telefoonnummer, adres en routebeschrijving.",
  sections: [
    {
      h2: "Hoe de rotatie werkt",
      body: "De Bond van Apothekers publiceert wekelijks een dienstrooster. Tijdens kantooruren zijn alle apotheken open; vanaf ongeveer 19:00 tot de volgende ochtend en de hele zondag is alleen de dienstapotheek beschikbaar voor spoedmedicatie. Sommige apotheken zijn 24 uur bemand — die worden hierboven aangegeven als 'Open 24 uur'.",
    },
    {
      h2: "Wat u vanaf deze pagina kunt doen",
      body: "Tik op het telefoonnummer om direct te bellen — de apotheker kan bevestigen dat uw medicijn op voorraad is voordat u de rit maakt. Tik op 'Routebeschrijving' om het adres in Google Maps te openen. Vraag bij een specifiek merk of dosering altijd van tevoren: apotheken helpen elkaar en kunnen u doorverwijzen.",
    },
    {
      h2: "Wat u moet meenemen",
      body: "Neem uw originele recept, ID of paspoort mee, en — als u AZV-verzekerd bent — uw AZV-pas. Contant en betaalkaarten worden geaccepteerd.",
    },
    {
      h2: "Noodnummers",
      body: "Voor levensbedreigende noodgevallen belt u 911. Voor niet-spoedeisend medisch advies buiten de openingstijden is de spoedeisende hulp van het Horacio Oduber Hospital in Oranjestad 24/7 open.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Alle Aruba-nieuws →" },
    { href: "/aruba-dagelijkse-briefing", label: "Aruba Dagelijkse Briefing" },
    { href: "/cruiseschepen-aruba-vandaag", label: "Cruiseschepen in Aruba vandaag" },
  ],
};

const PHARMACY_ES = {
  slug: "farmacia-turno-aruba",
  lang: "es",
  embedType: "pharmacy-duty",
  embedProps: { island: "aruba" },
  title: "Farmacia de Turno en Aruba Hoy — Rotación en Vivo | IslandsDaily",
  description:
    "¿Qué farmacia está de turno esta noche en Aruba? Actualizada a diario con nombre, dirección, teléfono y horarios. Llamada directa y ruta en Google Maps.",
  h1: "Farmacia de turno en Aruba hoy",
  intro:
    "Las farmacias de Aruba se turnan para cubrir noches y fines de semana, de modo que siempre hay una 'botica di turno' abierta. Esta página muestra qué farmacia tiene el turno ahora mismo, con teléfono, dirección y ruta.",
  sections: [
    {
      h2: "Cómo funciona el turno",
      body: "La Asociación de Farmacias de Aruba publica un rol semanal. En horario normal todas las farmacias están abiertas; desde aproximadamente las 19:00 hasta la mañana siguiente, y todo el domingo, solo la farmacia de turno atiende recetas de emergencia. Algunas farmacias operan 24 horas — se indican arriba como 'Open 24 hours'.",
    },
    {
      h2: "Qué puede hacer desde esta página",
      body: "Toque el teléfono para llamar directamente — el farmacéutico puede confirmar que su medicamento está en stock antes de que cruce la isla. Toque 'Get Directions' para abrir la dirección en Google Maps. Si necesita una marca o dosis específica, llame antes: las farmacias colaboran entre sí y pueden derivarle.",
    },
    {
      h2: "Qué llevar",
      body: "Lleve su receta original, documento de identidad o pasaporte, y — si tiene seguro AZV — su tarjeta AZV. Se acepta efectivo y tarjetas.",
    },
    {
      h2: "Números de emergencia",
      body: "Para emergencias que pongan en riesgo la vida, llame al 911. Para consulta médica no urgente fuera del horario, la sala de emergencias del Horacio Oduber Hospital en Oranjestad opera 24/7.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Todas las noticias de Aruba →" },
    { href: "/resumen-diario-aruba", label: "Resumen Diario de Aruba" },
    { href: "/cruceros-aruba-hoy", label: "Cruceros en Aruba hoy" },
  ],
};

// ------------ Cruise ships today Aruba -----------------------------
const CRUISE_EN = {
  slug: "cruise-ships-today-aruba",
  lang: "en",
  embedType: "cruise-today",
  embedProps: { island: "aruba" },
  title: "Cruise Ships in Aruba Today — Live Port Schedule | IslandsDaily",
  description:
    "Which cruise ships are in Oranjestad today? Live schedule with ship names, cruise lines, arrival and departure times, and passenger counts. Updated daily.",
  h1: "Cruise ships in Aruba today",
  intro:
    "Aruba's cruise terminal in Oranjestad handles more than 800,000 passengers a year. This page shows exactly which ships are docked right now, when they arrive and depart, and how many passengers are on board.",
  sections: [
    {
      h2: "Where the data comes from",
      body: "The schedule is sourced from Aruba Ports Authority's public port calls list and cross-referenced against the cruise lines' own itineraries so that ship names, cruise lines and passenger capacities are always current. If a ship's arrival is delayed we update within the hour.",
    },
    {
      h2: "Why the schedule matters",
      body: "For downtown restaurants, tour operators and taxi dispatchers the cruise schedule determines the entire rhythm of the day. For visitors on the ship the schedule matters because Oranjestad's Main Street shuts down around departure time and you don't want to be the last table ordering lunch when the horn sounds. For residents driving into town the schedule tells you when to expect the LG Smith Boulevard traffic wave.",
    },
    {
      h2: "How to use this page",
      body: "The card at the top of the page shows today's arrivals in order. Each row lists ship name, cruise line, arrival and departure times, and the number of passengers on board. Tap 'Full port schedule' to see the next seven days — useful if you're planning a shore-excursion booking or a tour company managing capacity.",
    },
    {
      h2: "Cruise season in Aruba",
      body: "High season runs November through April. Wednesdays and Saturdays are typically the busiest days at the terminal. In shoulder season some days see zero calls and the town breathes again — locals joke that it's the only time you can actually get a walk-in table at Zeerovers.",
    },
  ],
  faq: [
    {
      q: "How many cruise ships are in Aruba today?",
      a: "The live count is shown in the card above. On a typical high-season day expect two to four ships; some Wednesdays reach five.",
    },
    {
      q: "What time do cruise ships arrive in Aruba?",
      a: "Most ships arrive between 07:00 and 09:00 AST and depart between 17:00 and 22:00. Exact times per ship are listed above.",
    },
    {
      q: "Where do cruise ships dock in Aruba?",
      a: "At Oranjestad Cruise Terminal (Aruba Ports Authority), a short walk from Main Street. The terminal has three berths.",
    },
    {
      q: "Which cruise lines call in Aruba?",
      a: "Regulars include Carnival, Royal Caribbean, Norwegian, Celebrity, Holland America, MSC and Princess. Occasional calls from Disney, Virgin Voyages and smaller luxury lines.",
    },
  ],
  relatedLinks: [
    { href: "/aruba/cruise-schedule", label: "Full 7-day cruise schedule →" },
    { href: "/aruba", label: "All Aruba news" },
    { href: "/aruba-daily-briefing", label: "Aruba Daily Briefing" },
  ],
};

const CRUISE_NL = {
  slug: "cruiseschepen-aruba-vandaag",
  lang: "nl",
  embedType: "cruise-today",
  embedProps: { island: "aruba" },
  title: "Cruiseschepen in Aruba Vandaag — Live Havenrooster | IslandsDaily",
  description:
    "Welke cruiseschepen liggen vandaag in Oranjestad? Live rooster met scheepsnamen, rederijen, aankomst- en vertrektijden, en passagiersaantallen.",
  h1: "Cruiseschepen in Aruba vandaag",
  intro:
    "De cruiseterminal in Oranjestad verwerkt jaarlijks meer dan 800.000 passagiers. Deze pagina toont precies welke schepen nu aangemeerd liggen, hun aankomst- en vertrektijden en het aantal passagiers aan boord.",
  sections: [
    {
      h2: "Waar de data vandaan komt",
      body: "Het rooster komt van de openbare havenaanloopslijst van Aruba Ports Authority en wordt gekruist met de itineraries van de rederijen zodat namen, rederijen en capaciteiten altijd actueel zijn. Bij vertraging updaten we binnen een uur.",
    },
    {
      h2: "Waarom het rooster ertoe doet",
      body: "Voor restaurants in het centrum, tour-operators en taxidispatchers bepaalt het cruiserooster het volledige ritme van de dag. Voor bezoekers aan boord is het rooster belangrijk omdat Oranjestad's Main Street rond vertrektijd sluit. Voor bewoners geeft het aan wanneer de LG Smith Boulevard vast staat.",
    },
    {
      h2: "Hoe deze pagina te gebruiken",
      body: "De kaart bovenaan toont de aankomsten van vandaag op volgorde. Elke regel bevat scheepsnaam, rederij, aankomst- en vertrektijd en passagiersaantal. Tik op 'Volledig havenrooster' voor de komende zeven dagen.",
    },
    {
      h2: "Cruiseseizoen op Aruba",
      body: "Hoogseizoen loopt van november tot april. Woensdag en zaterdag zijn doorgaans de drukste dagen. In het tussenseizoen zijn er dagen zonder aanloop en ademt de stad weer.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba/cruise-schedule", label: "Volledig 7-daags cruiserooster →" },
    { href: "/aruba", label: "Alle Aruba-nieuws" },
    { href: "/aruba-dagelijkse-briefing", label: "Aruba Dagelijkse Briefing" },
  ],
};

const CRUISE_ES = {
  slug: "cruceros-aruba-hoy",
  lang: "es",
  embedType: "cruise-today",
  embedProps: { island: "aruba" },
  title: "Cruceros en Aruba Hoy — Horario del Puerto en Vivo | IslandsDaily",
  description:
    "¿Qué cruceros están en Oranjestad hoy? Horario en vivo con nombres de barcos, líneas, horarios de llegada y salida, y cantidad de pasajeros.",
  h1: "Cruceros en Aruba hoy",
  intro:
    "La terminal de cruceros de Oranjestad recibe más de 800.000 pasajeros al año. Esta página muestra exactamente qué barcos están atracados ahora mismo, cuándo llegan y salen, y cuántos pasajeros llevan a bordo.",
  sections: [
    {
      h2: "De dónde vienen los datos",
      body: "El horario proviene de la lista pública de escalas de Aruba Ports Authority y se cruza con los itinerarios de las líneas de crucero para que los nombres, líneas y capacidades estén siempre al día. Si una llegada se retrasa, actualizamos en menos de una hora.",
    },
    {
      h2: "Por qué el horario importa",
      body: "Para restaurantes del centro, operadores turísticos y taxistas, el horario de cruceros define todo el ritmo del día. Para pasajeros a bordo importa porque la Main Street de Oranjestad cierra alrededor de la hora de salida. Para residentes indica cuándo esperar el tráfico en LG Smith Boulevard.",
    },
    {
      h2: "Cómo usar esta página",
      body: "La tarjeta superior muestra las llegadas de hoy en orden. Cada fila lista nombre del barco, línea, horario de llegada y salida y pasajeros. Toque 'Horario completo del puerto' para ver los próximos siete días.",
    },
    {
      h2: "Temporada de cruceros en Aruba",
      body: "La temporada alta va de noviembre a abril. Miércoles y sábados suelen ser los días más ocupados. En temporada baja hay días sin escalas y la ciudad recupera su ritmo.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba/cruise-schedule", label: "Horario completo de 7 días →" },
    { href: "/aruba", label: "Todas las noticias de Aruba" },
    { href: "/resumen-diario-aruba", label: "Resumen Diario de Aruba" },
  ],
};

// ------------ Aruba daily briefing ---------------------------------
const ARUBA_BRIEFING_EN = {
  slug: "aruba-daily-briefing",
  lang: "en",
  embedType: "daily-top-stories",
  embedProps: { island: "aruba" },
  title: "Aruba Daily Briefing — Everything the Island Is Talking About | IslandsDaily",
  description:
    "One five-minute morning briefing covering Aruba's biggest stories in English. Politics, tourism, cruise, weather, safety — updated every morning at 08:00 AST.",
  h1: "Aruba Daily Briefing",
  intro:
    "Every morning at 08:00 AST we publish a five-minute briefing that covers the biggest things happening in Aruba. It's the fastest way to catch up if you don't have time to read the news in Papiamento or Dutch.",
  sections: [
    {
      h2: "What's in the briefing",
      body: "The daily briefing pulls the top-ranked headlines from our Aruba feed and presents them in reading order: what's happening in government, what's moving in tourism and cruise, any crime or safety alerts, the weather outlook, and a short community section. Five stories, roughly five minutes of reading.",
    },
    {
      h2: "How it's ranked",
      body: "Editorial ranking combines source diversity (a story covered by three outlets outranks one), recency, engagement on IslandsDaily (real reader clicks, not social vanity), and category weighting so that a major storm advisory or a parliamentary vote does not get buried under a lifestyle piece.",
    },
    {
      h2: "Delivered to WhatsApp",
      body: "If you'd rather have the briefing pushed to you, the WhatsApp Daily Digest sends the same five stories at 08:00 AST every morning. Free, no signup form, one tap to unsubscribe. The link is at the bottom of every page.",
    },
    {
      h2: "Read the sources",
      body: "Every briefing story links back to the original outlet — 24ora, Noticia Cla, AWE24, Mas Noticia — so you can go deeper on anything that catches your eye. IslandsDaily is a summary layer, not a replacement for local journalism.",
    },
  ],
  faq: [
    {
      q: "What time is the Aruba briefing published?",
      a: "The daily briefing goes live at 08:00 AST (Aruba local time). The WhatsApp Digest is delivered at the same moment.",
    },
    {
      q: "Is the briefing available in Dutch and Spanish?",
      a: "Yes. See /aruba-dagelijkse-briefing for Dutch and /resumen-diario-aruba for Spanish.",
    },
    {
      q: "How is this different from the main Aruba feed?",
      a: "The main feed lists every article we've scraped, ordered by time. The briefing is a hand-ranked top five so you can skim in five minutes.",
    },
    {
      q: "How do I subscribe on WhatsApp?",
      a: "Tap the red 'Subscribe' button in the header. It's free, we send five stories a day, no more.",
    },
  ],
  relatedLinks: [
    { href: "/aruba", label: "All Aruba news →" },
    { href: "/aruba-news-english", label: "Aruba News in English" },
    { href: "/caribbean-morning-briefing", label: "Caribbean Morning Briefing" },
  ],
};

const ARUBA_BRIEFING_NL = {
  slug: "aruba-dagelijkse-briefing",
  lang: "nl",
  embedType: "daily-top-stories",
  embedProps: { island: "aruba" },
  title: "Aruba Dagelijkse Briefing — Alles Wat Speelt op het Eiland | IslandsDaily",
  description:
    "Elke ochtend een briefing van vijf minuten met de grootste Aruba-verhalen in het Nederlands. Politiek, toerisme, cruise, weer, veiligheid.",
  h1: "Aruba Dagelijkse Briefing",
  intro:
    "Elke ochtend om 08:00 AST publiceren we een briefing van vijf minuten met de belangrijkste zaken die spelen op Aruba. De snelste manier om bij te blijven zonder Papiaments of Engels te lezen.",
  sections: [
    {
      h2: "Wat er in de briefing staat",
      body: "De briefing haalt de top-koppen uit onze Aruba-feed in leesvolgorde: regering, toerisme en cruise, veiligheidsmeldingen, weersvooruitzicht en een kort community-blok. Vijf verhalen, ongeveer vijf minuten leestijd.",
    },
    {
      h2: "Hoe de ranking werkt",
      body: "De ranking combineert bronvariëteit (een verhaal dat door drie bronnen is gedekt gaat boven één), actualiteit, engagement op IslandsDaily en categorieweging zodat een storm-waarschuwing of parlementaire stemming niet onder een lifestyle-artikel verdwijnt.",
    },
    {
      h2: "Bezorging via WhatsApp",
      body: "Wilt u de briefing direct ontvangen? De WhatsApp Dagelijkse Briefing stuurt dezelfde vijf verhalen elke ochtend om 08:00 AST. Gratis, geen aanmeldformulier, één tik om af te melden.",
    },
    {
      h2: "Lees de bronnen",
      body: "Elk briefing-verhaal linkt terug naar de originele bron — 24ora, Noticia Cla, AWE24, Mas Noticia — zodat u dieper kunt duiken.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Alle Aruba-nieuws →" },
    { href: "/nieuws-aruba", label: "Aruba-nieuws in het Nederlands" },
    { href: "/caribisch-ochtend-briefing", label: "Caribische Ochtendbriefing" },
  ],
};

const ARUBA_BRIEFING_ES = {
  slug: "resumen-diario-aruba",
  lang: "es",
  embedType: "daily-top-stories",
  embedProps: { island: "aruba" },
  title: "Resumen Diario de Aruba — Todo lo que Pasa en la Isla | IslandsDaily",
  description:
    "Cada mañana un resumen de cinco minutos con las principales historias de Aruba en español. Política, turismo, cruceros, clima, seguridad.",
  h1: "Resumen Diario de Aruba",
  intro:
    "Cada mañana a las 08:00 AST publicamos un resumen de cinco minutos con lo más importante de Aruba. La forma más rápida de ponerse al día sin necesidad de leer en papiamento u holandés.",
  sections: [
    {
      h2: "Qué contiene el resumen",
      body: "El resumen selecciona los titulares principales de nuestro feed de Aruba en orden de lectura: gobierno, turismo y cruceros, alertas de seguridad, clima y una breve sección comunitaria. Cinco historias, unos cinco minutos de lectura.",
    },
    {
      h2: "Cómo se ordena",
      body: "El orden combina diversidad de fuentes (una historia cubierta por tres medios sube más que una), actualidad, clics reales en IslandsDaily y peso por categoría para que una alerta de tormenta o una votación parlamentaria no quede sepultada bajo una nota de estilo de vida.",
    },
    {
      h2: "Entregado por WhatsApp",
      body: "Si prefiere recibirlo, el Resumen Diario por WhatsApp envía las mismas cinco historias cada mañana a las 08:00 AST. Gratis, sin formulario, un toque para darse de baja.",
    },
    {
      h2: "Lea las fuentes",
      body: "Cada historia del resumen enlaza a la fuente original — 24ora, Noticia Cla, AWE24, Mas Noticia — para que pueda profundizar en cualquier tema.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/aruba", label: "Todas las noticias de Aruba →" },
    { href: "/noticias-aruba", label: "Noticias de Aruba en español" },
    { href: "/resumen-matutino-caribe", label: "Resumen Matutino del Caribe" },
  ],
};

// ------------ Caribbean morning briefing ---------------------------
const CARIBBEAN_EN = {
  slug: "caribbean-morning-briefing",
  lang: "en",
  embedType: "daily-top-stories",
  embedProps: { island: "all" },
  title: "The Caribbean Morning Briefing — Aruba, Curaçao, Bonaire, St. Maarten | IslandsDaily",
  description:
    "One morning briefing covering the Dutch Caribbean in English: Aruba, Curaçao, Bonaire and St. Maarten. Politics, tourism, cruise, weather. Free WhatsApp digest.",
  h1: "The Caribbean Morning Briefing",
  intro:
    "IslandsDaily's flagship product: one English morning briefing covering Aruba, Curaçao, Bonaire and St. Maarten. If the Dutch Caribbean matters to your work, your family or your holiday, this is the fastest way to know what's happening across all four islands before your first coffee.",
  sections: [
    {
      h2: "Four islands, one briefing",
      body: "Every morning at 08:00 AST we publish a cross-island briefing that draws the top-ranked stories from each of the four ABCS islands. Aruba's political story of the day, Curaçao's refinery update, Bonaire's tourism angle, St. Maarten's cruise arrival — surfaced in one place so you don't have to check five different sites in five different languages.",
    },
    {
      h2: "How we choose stories",
      body: "The cross-island ranking gives every island equal footing regardless of population, then rewards stories with multi-source coverage, reader engagement on IslandsDaily and category weight. A major storm anywhere in the region always outranks lighter fare. Sponsored content is never in the briefing — sponsorship supports the site but has no editorial influence.",
    },
    {
      h2: "Who reads it",
      body: "Tourists booking flights, second-home owners in the region, remote workers on 90-day visits, expats managing family from abroad, dive tour operators, real-estate agents, embassy staff, and a growing group of readers who simply care about the region. About 60% of readers open on mobile from outside the Caribbean; 30% from within the Kingdom of the Netherlands.",
    },
    {
      h2: "Free forever",
      body: "The site and the WhatsApp Daily Digest are free. IslandsDaily is funded by local advertising — small businesses in Oranjestad, Willemstad, Kralendijk and Philipsburg who want to reach English-speaking visitors and residents. No paywall, no email spam, no account.",
    },
  ],
  faq: [
    {
      q: "What is the Caribbean Morning Briefing?",
      a: "A short English news briefing covering Aruba, Curaçao, Bonaire and St. Maarten. Published every morning at 08:00 AST on IslandsDaily and delivered free by WhatsApp to subscribers.",
    },
    {
      q: "Does it cover Jamaica, Barbados or the rest of the Caribbean?",
      a: "The current briefing focuses on the Dutch Caribbean (ABCS: Aruba, Bonaire, Curaçao, St. Maarten). Wider Caribbean coverage is on the roadmap.",
    },
    {
      q: "How do I subscribe?",
      a: "Tap the red Subscribe button in the header. It sends five stories a day via WhatsApp. No signup form, one tap to unsubscribe.",
    },
    {
      q: "Where do the stories come from?",
      a: "Aruba: 24ora, Noticia Cla, AWE24, Mas Noticia. Curaçao: Nu.cw, Knipselkrant Curaçao, Curaçao Chronicle. Bonaire: InfoBonaire, Bonaire Island, Bonaire Reporter. St. Maarten: The Daily Herald, SXM Island Time, 721 News.",
    },
  ],
  relatedLinks: [
    { href: "/", label: "All Islands home →" },
    { href: "/aruba-news-english", label: "Aruba News in English" },
    { href: "/curacao-news-english", label: "Curaçao News in English" },
    { href: "/aruba-daily-briefing", label: "Aruba Daily Briefing" },
  ],
};

const CARIBBEAN_NL = {
  slug: "caribisch-ochtend-briefing",
  lang: "nl",
  embedType: "daily-top-stories",
  embedProps: { island: "all" },
  title: "De Caribische Ochtendbriefing — Aruba, Curaçao, Bonaire, St. Maarten | IslandsDaily",
  description:
    "Eén ochtendbriefing die het Caribisch deel van het Koninkrijk dekt in het Nederlands: Aruba, Curaçao, Bonaire en St. Maarten. Gratis WhatsApp-digest.",
  h1: "De Caribische Ochtendbriefing",
  intro:
    "Het vlaggenschipproduct van IslandsDaily: één Nederlandstalige ochtendbriefing die Aruba, Curaçao, Bonaire en St. Maarten dekt. Als het Caribisch deel van het Koninkrijk relevant is voor uw werk, familie of vakantie, is dit de snelste manier om bij te blijven vóór uw eerste kop koffie.",
  sections: [
    {
      h2: "Vier eilanden, één briefing",
      body: "Elke ochtend om 08:00 AST publiceren we een cross-island briefing met de best gerankte verhalen uit elk van de vier ABCS-eilanden. Politiek verhaal van Aruba, raffinaderij-update van Curaçao, toerisme-hoek van Bonaire, cruise-aankomst van St. Maarten — alles op één plek zodat u niet vijf sites in vijf talen hoeft af te struinen.",
    },
    {
      h2: "Hoe we verhalen kiezen",
      body: "De cross-island ranking geeft elk eiland gelijke voet ongeacht bevolkingsomvang en beloont vervolgens verhalen met multi-source dekking, engagement op IslandsDaily en categoriegewicht. Een grote storm in de regio staat altijd boven lichter nieuws. Gesponsorde content komt nooit in de briefing.",
    },
    {
      h2: "Wie het leest",
      body: "Toeristen die vluchten boeken, tweede-huiseigenaren, remote workers, expats met familie op afstand, duikondernemers, makelaars, ambassadepersoneel — en een groeiende groep lezers die simpelweg om de regio geeft.",
    },
    {
      h2: "Altijd gratis",
      body: "De site en de WhatsApp Dagelijkse Briefing zijn gratis. IslandsDaily wordt gefinancierd door lokale adverteerders — kleine bedrijven in Oranjestad, Willemstad, Kralendijk en Philipsburg. Geen paywall, geen e-mailspam, geen account.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/", label: "Alle eilanden →" },
    { href: "/nieuws-aruba", label: "Aruba-nieuws in het Nederlands" },
    { href: "/nieuws-curacao", label: "Curaçao-nieuws in het Nederlands" },
    { href: "/aruba-dagelijkse-briefing", label: "Aruba Dagelijkse Briefing" },
  ],
};

const CARIBBEAN_ES = {
  slug: "resumen-matutino-caribe",
  lang: "es",
  embedType: "daily-top-stories",
  embedProps: { island: "all" },
  title: "Resumen Matutino del Caribe — Aruba, Curazao, Bonaire, San Martín | IslandsDaily",
  description:
    "Un solo resumen matutino en español que cubre el Caribe holandés: Aruba, Curazao, Bonaire y San Martín. Política, turismo, cruceros, clima. Gratis por WhatsApp.",
  h1: "Resumen Matutino del Caribe",
  intro:
    "El producto insignia de IslandsDaily: un resumen matutino en español que cubre Aruba, Curazao, Bonaire y San Martín. Si el Caribe holandés importa para su trabajo, su familia o sus vacaciones, esta es la forma más rápida de saber qué pasa en las cuatro islas antes de su primer café.",
  sections: [
    {
      h2: "Cuatro islas, un resumen",
      body: "Cada mañana a las 08:00 AST publicamos un resumen entre islas que reúne las historias mejor puntuadas de cada una de las cuatro islas ABCS. Historia política del día de Aruba, actualización de la refinería en Curazao, ángulo turístico de Bonaire, llegada de crucero en San Martín — todo en un solo lugar.",
    },
    {
      h2: "Cómo elegimos historias",
      body: "El ranking entre islas da igualdad de peso a cada isla sin importar el tamaño de la población y luego premia las historias con cobertura de múltiples fuentes, engagement real en IslandsDaily y peso por categoría. Una tormenta importante en la región siempre supera al contenido ligero.",
    },
    {
      h2: "Quién lo lee",
      body: "Turistas que reservan vuelos, dueños de segundas viviendas, trabajadores remotos, expatriados con familia a distancia, operadores de buceo, agentes inmobiliarios, personal de embajadas — y un grupo creciente de lectores que simplemente se interesan por la región.",
    },
    {
      h2: "Siempre gratis",
      body: "El sitio y el Resumen Diario por WhatsApp son gratis. IslandsDaily se financia con publicidad local — pequeños negocios en Oranjestad, Willemstad, Kralendijk y Philipsburg. Sin muro de pago, sin spam por correo, sin cuenta.",
    },
  ],
  faq: null,
  relatedLinks: [
    { href: "/", label: "Todas las islas →" },
    { href: "/noticias-aruba", label: "Noticias de Aruba en español" },
    { href: "/noticias-curazao", label: "Noticias de Curazao en español" },
    { href: "/resumen-diario-aruba", label: "Resumen Diario de Aruba" },
  ],
};

// ------------- Full list -------------------------------------------
const PAGES = [
  ARUBA_NEWS_EN, ARUBA_NEWS_NL, ARUBA_NEWS_ES,
  CURACAO_NEWS_EN, CURACAO_NEWS_NL, CURACAO_NEWS_ES,
  PHARMACY_EN, PHARMACY_NL, PHARMACY_ES,
  CRUISE_EN, CRUISE_NL, CRUISE_ES,
  ARUBA_BRIEFING_EN, ARUBA_BRIEFING_NL, ARUBA_BRIEFING_ES,
  CARIBBEAN_EN, CARIBBEAN_NL, CARIBBEAN_ES,
];

// Topic groups — each row is [EN slug, NL slug, ES slug] for one topic.
// Used to emit hreflang alternate <link> tags between language variants.
// Mirror in backend/seo_landing_pages.py TOPIC_GROUPS.
export const TOPIC_GROUPS = [
  [ARUBA_NEWS_EN.slug,    ARUBA_NEWS_NL.slug,    ARUBA_NEWS_ES.slug],
  [CURACAO_NEWS_EN.slug,  CURACAO_NEWS_NL.slug,  CURACAO_NEWS_ES.slug],
  [PHARMACY_EN.slug,      PHARMACY_NL.slug,      PHARMACY_ES.slug],
  [CRUISE_EN.slug,        CRUISE_NL.slug,        CRUISE_ES.slug],
  [ARUBA_BRIEFING_EN.slug, ARUBA_BRIEFING_NL.slug, ARUBA_BRIEFING_ES.slug],
  [CARIBBEAN_EN.slug,     CARIBBEAN_NL.slug,     CARIBBEAN_ES.slug],
];

// Returns hreflang alternate tag descriptors for a given slug: an entry
// per language sibling plus x-default → the English sibling.
export const getHreflangAlternates = (slug) => {
  const group = TOPIC_GROUPS.find((g) => g.includes(slug));
  if (!group) return [];
  const [enSlug, nlSlug, esSlug] = group;
  return [
    { hreflang: "en", href: `${SITE}/${enSlug}` },
    { hreflang: "nl", href: `${SITE}/${nlSlug}` },
    { hreflang: "es", href: `${SITE}/${esSlug}` },
    { hreflang: "x-default", href: `${SITE}/${enSlug}` },
  ];
};

// Attach canonical
export const SEO_PAGES = PAGES.map((p) => ({
  ...p,
  canonical: `${SITE}/${p.slug}`,
}));

export const SEO_PAGES_BY_SLUG = SEO_PAGES.reduce((acc, p) => {
  acc[p.slug] = p;
  return acc;
}, {});
